import {ContentHotel} from '@findhotel/sapi'
import {PolicySubSection} from 'components/Accommodation/Policies/types'
import {PriceTrendsTrackingContext} from 'components/PriceTrend/types'
import {Hotel} from 'modules/sapiSearch/slice'

import {OfferCheck} from '@vio/core/src/api-types/bovio/response/offer_check'
import {Placement} from '@vio/core/src/deepLinking/types'
import {Room} from '@vio/core/src/room/types/room'
import {PriceBreakdownModalProps} from '@vio/shared/src/booking/PriceBreakdownModal'

export enum OverlayType {
  HotelDescription = 'hotelDescription',
  HotelFacilities = 'hotelFacilities',
  HotelPolicies = 'hotelPolicies',
  HotelDatePicker = 'hotelDatePicker',
  HotelGuestPicker = 'hotelGuestPicker',
  HotelAllOffers = 'hotelAllOffers',
  Filters = 'filters',
  PriceFilter = 'priceFilter',
  FacilitiesFilter = 'facilitiesFilter',
  SortBy = 'sortBy',
  DestinationPicker = 'destinationPicker',
  DatePicker = 'datePicker',
  GuestPicker = 'guestPicker',
  Preferences = 'preferences',
  LightboxGalleryOverlay = 'lightboxGalleryOverlay',
  LocationOverlay = 'hotelLocationOverlay',
  LocationInfoOverlay = 'hotelLocationInfoOverlay',
  OtherOverlay = 'otherOverlay',
  VerticalGalleryOverlay = 'VerticalGalleryOverlay',
  SplitBookingInfoBottomSheet = 'SplitBookingInfoBottomSheet',
  RoomDetailsOverlay = 'RoomDetailsOverlay',
  RoomOfferPriceBreakdownOverlay = 'RoomOfferPriceBreakdownOverlay',
  ShareYourFeedbackOverlay = 'ShareYourFeedbackOverlay',
  ReviewScoreFilter = 'reviewScoreFilter',
  StarRatingsFilter = 'starRatingsFilter',
  AvailabilityPopup = 'availabilityPopup',
  PropertyTypeFilter = 'propertyTypeFilter',
  Web2AppPopup = 'web2AppPopup',
  GuestReviews = 'guestReviews',
  StreetView = 'streetView',
  PriceWatchOverlay = 'priceWatch',
  AiPoweredReviewsPanel = 'AiPoweredReviewsPanel',
  HowSearchWorks = 'HowSearchWorks',
  DealDetails = 'DealDetails',
  CashbackInfo = 'CashbackInfo',
  FreezeDetails = 'FreezeDetails',
  FreezeGuestDetails = 'FreezeGuestDetails',
  PriceTrendBottomSheet = 'PriceTrendBottomSheet',
  TrustpilotBottomSheet = 'TrustpilotBottomSheet',
  BookOnAppConfirmation = 'BookOnAppConfirmation'
}

export enum DateGuestPickerSource {
  AccommodationPageSearch = 'apSearch',
  HotelViewRoomsSection = 'hotelViewRoomsSection'
}

export type HotelFacilitiesOverlayInfo = {
  type: OverlayType.HotelFacilities
  hotelId: Hotel['objectID']
}

export type PriceWatchOverlayInfo = {
  type: OverlayType.PriceWatchOverlay
  hotel: Hotel | ContentHotel
  rate: {
    baseRate: number
    localTaxes: number
    taxes: number
    nightlyRate: number
    totalRate: number
    localTaxesIncluded: boolean
    taxesIncluded: boolean
    currency: string
    shouldShowTotal: boolean
  }
  flow?: string
  priceTrendsContext?: PriceTrendsTrackingContext
}

export type HotelPoliciesOverlayInfo = {
  type: OverlayType.HotelPolicies
  policySubSection: PolicySubSection
}

export type LightboxGalleryOverlayInfo = {
  type: OverlayType.LightboxGalleryOverlay
  hotelId: Hotel['objectID']
  initialImageIndex: number
  images: string[]
  selectedCategory?: string
}

export type HotelAllOffersOverlayInfo = {
  type: OverlayType.HotelAllOffers
  hotelId: Hotel['objectID']
}

export type HotelDatePickerOverlayInfo = {
  type: OverlayType.HotelDatePicker
  source: DateGuestPickerSource
}

export type HotelGuestPickerOverlayInfo = {
  type: OverlayType.HotelGuestPicker
  source: DateGuestPickerSource
}

export type RoomDetailsOverlayInfo = {
  type: OverlayType.RoomDetailsOverlay
  roomId: Room['id']
  hotelId: Hotel['objectID']
}

export type Web2AppPopupOverlayInfo = {
  type: OverlayType.Web2AppPopup
  hotelId?: Hotel['objectID']
  placement: Placement
  clickedOfferRate?: number
}

export type RoomOfferPriceBreakdownOverlayParams = Omit<
  PriceBreakdownModalProps,
  'isOpen' | 'onClose'
>
export type RoomOfferPriceBreakdownOverlayInfo = {
  type: OverlayType.RoomOfferPriceBreakdownOverlay
} & RoomOfferPriceBreakdownOverlayParams

export type GuestReviewsOverlayInfo = {
  type: OverlayType.GuestReviews
}

export type StreetViewOverlayInfo = {
  type: OverlayType.StreetView
  hotelId: string
}

export type DealDetailsOverlayInfo = {
  type: OverlayType.DealDetails
  roomId?: string
  offerId?: string
  bundleId?: string
  isFrozen?: boolean
}

export type BookOnAppConfirmationOverlayInfo = {
  type: OverlayType.BookOnAppConfirmation
  roomId: string
  offerId?: string
  placement?: Placement
  clickedOfferRate: number
  currency: string
  /** Offer check response, so the sheet shows the checked price */
  offerCheck: Pick<
    OfferCheck,
    'checkIn' | 'checkOut' | 'prices' | 'paymentMode'
  >
}

export type CashbackInfoOverlayInfo = {
  type: OverlayType.CashbackInfo
  value: number
}

export type PriceTrendBottomSheetOverlayInfo = {
  type: OverlayType.PriceTrendBottomSheet
  /** Where the sheet was opened from, used for analytics attribution. */
  source?: 'banner' | 'button'
}

/**
 * Union any overlays with parameters to allow for a type safe lookup
 * This is used for:
 * - overlay selectors to return correct parameters
 * - showOverlay, so enforce correct parameters are passed
 */
export type Overlay = {
  id?: string
} & (
  | {
      type: OverlayType
    }
  | HotelPoliciesOverlayInfo
  | HotelFacilitiesOverlayInfo
  | HotelAllOffersOverlayInfo
  | LightboxGalleryOverlayInfo
  | HotelDatePickerOverlayInfo
  | HotelGuestPickerOverlayInfo
  | RoomDetailsOverlayInfo
  | RoomOfferPriceBreakdownOverlayInfo
  | Web2AppPopupOverlayInfo
  | GuestReviewsOverlayInfo
  | PriceWatchOverlayInfo
  | StreetViewOverlayInfo
  | DealDetailsOverlayInfo
  | BookOnAppConfirmationOverlayInfo
  | CashbackInfoOverlayInfo
  | PriceTrendBottomSheetOverlayInfo
)
